import { create } from 'zustand';
import { debounce } from 'lodash-es';
import type { Client } from '@microsoft/microsoft-graph-client';
import { readFileContent, writeFileContent, listFolder } from '@/lib/graph/onedrive-helpers';

/**
 * Evidence item interface
 */
export interface Evidence {
  id: string;
  userId: string;
  weekKey: string;
  title: string;
  description: string;
  outcome?: string;
  source: 'graph_files' | 'graph_chat' | 'graph_email' | 'conversation' | 'upload_screenshot' | 'upload_document';
  category: 'accomplishment' | 'project_progress' | 'meeting' | 'collaboration' | 'learning';
  confidence: 'high' | 'medium' | 'low';
  timestamp: number;
  capturedAt: number;
  people: string[];
  projects: string[];
  keywords: string[];
  sourceUrl?: string;
  sourceId?: string;
  uploadedFileUrl?: string;
  uploadedFileName?: string;
  extractedText?: string;
  conversationTurn?: string;
}

/**
 * Recent week summary
 */
export interface RecentWeek {
  weekKey: string;
  status: 'draft' | 'approved' | 'archived';
  lastModified: number;
}

/**
 * Document status types
 */
export type DocumentStatus = 'draft' | 'approved' | 'archived';
export type EditorMode = 'edit' | 'preview';
export type AutoSaveStatus = 'idle' | 'saving' | 'saved' | 'error';

/**
 * Compute the current ISO week key (e.g., "2026-W03")
 */
export function computeCurrentWeekKey(): string {
  const now = new Date();
  const year = now.getFullYear();
  
  // Get the week number (ISO 8601)
  const startOfYear = new Date(year, 0, 1);
  const days = Math.floor((now.getTime() - startOfYear.getTime()) / (24 * 60 * 60 * 1000));
  const weekNum = Math.ceil((days + startOfYear.getDay() + 1) / 7);
  
  return `${year}-W${String(weekNum).padStart(2, '0')}`;
}

/**
 * Get week date range for display
 */
export function getWeekDateRange(weekKey: string): { start: Date; end: Date } {
  const match = weekKey.match(/(\d{4})-W(\d{2})/);
  if (!match) {
    return { start: new Date(), end: new Date() };
  }
  
  const year = parseInt(match[1]);
  const week = parseInt(match[2]);
  
  // Get January 4th of the year (always in week 1)
  const jan4 = new Date(year, 0, 4);
  
  // Get the Monday of week 1
  const dayOfWeek = jan4.getDay() || 7;
  const week1Monday = new Date(jan4);
  week1Monday.setDate(jan4.getDate() - dayOfWeek + 1);
  
  // Calculate the Monday of the target week
  const start = new Date(week1Monday);
  start.setDate(week1Monday.getDate() + (week - 1) * 7);
  
  // Friday is 4 days after Monday
  const end = new Date(start);
  end.setDate(start.getDate() + 4);
  
  return { start, end };
}

/**
 * Calculate days into the week (1-5 for Mon-Fri)
 */
export function getDaysIntoWeek(): number {
  const dayOfWeek = new Date().getDay();
  // Sunday = 0, so Monday = 1, etc.
  if (dayOfWeek === 0 || dayOfWeek === 6) return 5; // Weekend shows as complete week
  return dayOfWeek;
}

/**
 * Navigate to adjacent week
 */
export function getAdjacentWeekKey(weekKey: string, direction: -1 | 1): string {
  const match = weekKey.match(/(\d{4})-W(\d{2})/);
  if (!match) return weekKey;
  
  let year = parseInt(match[1]);
  let week = parseInt(match[2]) + direction;
  
  // Handle year boundaries
  if (week < 1) {
    year -= 1;
    week = 52; // Simplified - some years have 53 weeks
  } else if (week > 52) {
    year += 1;
    week = 1;
  }
  
  return `${year}-W${String(week).padStart(2, '0')}`;
}

interface DocumentState {
  // Current document
  currentWeekKey: string;
  documentContent: string;
  documentStatus: DocumentStatus;
  editorMode: EditorMode;
  autoSaveStatus: AutoSaveStatus;
  isLoadingDocument: boolean;
  
  // Evidence
  evidence: Evidence[];
  evidenceCount: number;
  newEvidenceToday: number;
  isLoadingEvidence: boolean;
  
  // Stats
  lastEditTime: number;
  matchedKeywords: string[];
  peopleCount: number;
  projectCount: number;
  
  // Recent weeks
  recentWeeks: RecentWeek[];
  
  // Graph client reference
  graphClient: Client | null;
  userId: string | null;
  
  // Actions
  initialize: (graph: Client, userId: string) => void;
  setWeekKey: (weekKey: string) => void;
  navigateWeek: (direction: -1 | 1) => void;
  setDocumentContent: (content: string) => void;
  setEditorMode: (mode: EditorMode) => void;
  saveDocument: () => Promise<void>;
  finalizeDocument: () => Promise<void>;
  loadDocument: (weekKey: string) => Promise<void>;
  loadEvidence: (weekKey: string) => Promise<void>;
  loadRecentWeeks: () => Promise<void>;
  addEvidence: (evidence: Evidence) => Promise<void>;
}

// Create debounced save function outside the store
let debouncedSave: (() => void) | null = null;

export const useDocumentStore = create<DocumentState>((set, get) => ({
  // Initial state
  currentWeekKey: computeCurrentWeekKey(),
  documentContent: '',
  documentStatus: 'draft',
  editorMode: 'edit',
  autoSaveStatus: 'idle',
  isLoadingDocument: false,
  evidence: [],
  evidenceCount: 0,
  newEvidenceToday: 0,
  isLoadingEvidence: false,
  lastEditTime: 0,
  matchedKeywords: [],
  peopleCount: 0,
  projectCount: 0,
  recentWeeks: [],
  graphClient: null,
  userId: null,

  initialize: (graph: Client, userId: string) => {
    set({ graphClient: graph, userId });
    
    // Initialize debounced save
    debouncedSave = debounce(() => {
      get().saveDocument();
    }, 2000);
    
    // Load current week's document
    const weekKey = get().currentWeekKey;
    get().loadDocument(weekKey);
    get().loadEvidence(weekKey);
    get().loadRecentWeeks();
  },

  setWeekKey: (weekKey: string) => {
    set({ currentWeekKey: weekKey, autoSaveStatus: 'idle' });
    get().loadDocument(weekKey);
    get().loadEvidence(weekKey);
  },

  navigateWeek: (direction: -1 | 1) => {
    const { currentWeekKey } = get();
    const newWeekKey = getAdjacentWeekKey(currentWeekKey, direction);
    get().setWeekKey(newWeekKey);
  },

  setDocumentContent: (content: string) => {
    set({ documentContent: content });
    // Trigger auto-save
    if (debouncedSave) {
      debouncedSave();
    }
  },

  setEditorMode: (mode: EditorMode) => {
    set({ editorMode: mode });
  },

  saveDocument: async () => {
    const { graphClient, currentWeekKey, documentContent, documentStatus } = get();
    if (!graphClient) return;

    set({ autoSaveStatus: 'saving' });
    
    try {
      const filePath = `weekly/${currentWeekKey}.md`;
      
      // Build the document with metadata header
      const now = new Date().toISOString();
      const fullContent = `---
weekKey: ${currentWeekKey}
status: ${documentStatus}
lastModified: ${now}
---

${documentContent}`;

      await writeFileContent(graphClient, filePath, fullContent);
      
      set({ 
        autoSaveStatus: 'saved', 
        lastEditTime: Date.now() 
      });
      
      // Reset status after 2 seconds
      setTimeout(() => {
        const current = get().autoSaveStatus;
        if (current === 'saved') {
          set({ autoSaveStatus: 'idle' });
        }
      }, 2000);
    } catch (error) {
      console.error('Failed to save document:', error);
      set({ autoSaveStatus: 'error' });
    }
  },

  finalizeDocument: async () => {
    const { graphClient, currentWeekKey, documentContent } = get();
    if (!graphClient) return;

    try {
      const filePath = `weekly/${currentWeekKey}.md`;
      const now = new Date().toISOString();
      
      const fullContent = `---
weekKey: ${currentWeekKey}
status: approved
lastModified: ${now}
finalizedAt: ${now}
---

${documentContent}`;

      await writeFileContent(graphClient, filePath, fullContent);
      
      set({ documentStatus: 'approved' });
      
      // Refresh recent weeks list
      get().loadRecentWeeks();
    } catch (error) {
      console.error('Failed to finalize document:', error);
      throw error;
    }
  },

  loadDocument: async (weekKey: string) => {
    const { graphClient } = get();
    if (!graphClient) return;

    set({ isLoadingDocument: true });
    
    try {
      const filePath = `weekly/${weekKey}.md`;
      const content = await readFileContent(graphClient, filePath);
      
      // Parse frontmatter and content
      let documentContent = '';
      let status: DocumentStatus = 'draft';
      let lastModified = 0;
      
      const contentStr = typeof content === 'string' ? content : '';
      
      // Check for frontmatter
      const frontmatterMatch = contentStr.match(/^---\n([\s\S]*?)\n---\n([\s\S]*)$/);
      if (frontmatterMatch) {
        const frontmatter = frontmatterMatch[1];
        documentContent = frontmatterMatch[2].trim();
        
        // Parse status from frontmatter
        const statusMatch = frontmatter.match(/status:\s*(\w+)/);
        if (statusMatch) {
          status = statusMatch[1] as DocumentStatus;
        }
        
        // Parse lastModified
        const modifiedMatch = frontmatter.match(/lastModified:\s*(.+)/);
        if (modifiedMatch) {
          lastModified = new Date(modifiedMatch[1]).getTime();
        }
      } else {
        documentContent = contentStr;
      }
      
      set({ 
        documentContent, 
        documentStatus: status,
        lastEditTime: lastModified,
        isLoadingDocument: false 
      });
    } catch (error) {
      // File doesn't exist - create a new document template
      const defaultContent = `# Weekly Summary - ${weekKey}

## Summary
Summarize your key accomplishments this week.

## Highlights
- 

## Impact
- 

## Next Steps
- 
`;
      
      set({ 
        documentContent: defaultContent, 
        documentStatus: 'draft',
        lastEditTime: 0,
        isLoadingDocument: false 
      });
    }
  },

  loadEvidence: async (weekKey: string) => {
    const { graphClient } = get();
    if (!graphClient) return;

    set({ isLoadingEvidence: true });
    
    try {
      // Try to load evidence from the evidence folder
      const evidencePath = `evidence/${weekKey}`;
      
      try {
        const files = await listFolder(graphClient, evidencePath);
        const evidence: Evidence[] = [];
        
        for (const file of files) {
          if (file.name.endsWith('.json')) {
            try {
              const content = await readFileContent(graphClient, `${evidencePath}/${file.name}`);
              const parsed = typeof content === 'string' ? JSON.parse(content) : content;
              evidence.push(parsed as Evidence);
            } catch {
              // Skip invalid files
            }
          }
        }
        
        // Calculate stats
        const peopleSet = new Set<string>();
        const projectSet = new Set<string>();
        const keywordSet = new Set<string>();
        
        evidence.forEach(e => {
          e.people?.forEach(p => peopleSet.add(p));
          e.projects?.forEach(p => projectSet.add(p));
          e.keywords?.forEach(k => keywordSet.add(k));
        });
        
        const today = new Date().toISOString().split('T')[0];
        const newToday = evidence.filter(e =>
          new Date(e.capturedAt).toISOString().split('T')[0] === today
        ).length;
        
        set({
          evidence,
          evidenceCount: evidence.length,
          newEvidenceToday: newToday,
          peopleCount: peopleSet.size,
          projectCount: projectSet.size,
          matchedKeywords: Array.from(keywordSet),
          isLoadingEvidence: false
        });
      } catch {
        // No evidence folder yet
        set({
          evidence: [],
          evidenceCount: 0,
          newEvidenceToday: 0,
          peopleCount: 0,
          projectCount: 0,
          matchedKeywords: [],
          isLoadingEvidence: false
        });
      }
    } catch (error) {
      console.error('Failed to load evidence:', error);
      set({ isLoadingEvidence: false });
    }
  },

  loadRecentWeeks: async () => {
    const { graphClient, currentWeekKey } = get();
    if (!graphClient) return;
    
    try {
      const files = await listFolder(graphClient, 'weekly');
      
      const weeks: RecentWeek[] = [];
      
      for (const file of files) {
        if (file.name.endsWith('.md')) {
          const weekKey = file.name.replace('.md', '');
          
          // Skip current week (it's shown in the editor)
          if (weekKey === currentWeekKey) continue;
          
          // Try to read status from file
          let status: DocumentStatus = 'draft';
          try {
            const content = await readFileContent(graphClient, `weekly/${file.name}`);
            const contentStr = typeof content === 'string' ? content : '';
            const statusMatch = contentStr.match(/status:\s*(\w+)/);
            if (statusMatch) {
              status = statusMatch[1] as DocumentStatus;
            }
          } catch {
            // Use default status
          }
          
          weeks.push({
            weekKey,
            status,
            lastModified: new Date(file.lastModifiedDateTime).getTime()
          });
        }
      }
      
      // Sort by week key (most recent first)
      weeks.sort((a, b) => b.weekKey.localeCompare(a.weekKey));
      
      // Keep only recent 5 weeks
      set({ recentWeeks: weeks.slice(0, 5) });
    } catch {
      // No weekly folder yet
      set({ recentWeeks: [] });
    }
  },

  addEvidence: async (evidence: Evidence) => {
    const { graphClient, currentWeekKey } = get();
    if (!graphClient) return;
    
    try {
      const evidencePath = `evidence/${currentWeekKey}/${evidence.id}.json`;
      await writeFileContent(graphClient, evidencePath, JSON.stringify(evidence, null, 2));
      
      // Reload evidence
      await get().loadEvidence(currentWeekKey);
    } catch (error) {
      console.error('Failed to add evidence:', error);
      throw error;
    }
  },
}));
